'use client'

import { useState, FormEvent } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { Button, LinkButton } from '@/components/ui/button'
import Arrow from '@/components/marketing/Arrow'
import { Input } from '@/components/marketing/Input'
import { Mail, MapPin, Send, ChevronDown } from 'lucide-react'
import DirectContactStrip from '@/components/marketing/DirectContactStrip'
import { FaLinkedinIn } from 'react-icons/fa6'
import { getVersion } from '@/lib/version'
import { reopenConsentBanner } from '@/lib/consent-manager'
import './footer-adapt.css'

interface FooterProps {
  showCTA?: boolean
}

const BUILT_WITH = [
  'Next.js',
  'TypeScript',
  'Tailwind',
  'Vercel',
  'Supabase',
  'AWS',
  'OpenAI',
  'Claude Code',
  'LangChain',
  'Langfuse',
  'Pinecone',
  'Resend',
  'Paddle',
  'Snyk',
]

const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy-policy' },
  { label: 'Terms', href: '/terms-of-service' },
  { label: 'Refunds', href: '/refund-policy' },
  { label: 'Cookies', href: '/cookie-policy' },
  { label: 'AI Policy', href: '/ai-policy' },
]

export default function Footer({ showCTA }: FooterProps) {
  const pathname = usePathname()
  const currentYear = 2026 // Hardcoded for 2026 launch
  const [openSections, setOpenSections] = useState<string[]>([])

  // Newsletter form state
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!email || isSubmitting) return

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer_form' }),
      })

      const data = await response.json()

      if (response.ok) {
        setSubmitStatus('success')
        setSubmitMessage(data.message || "You're subscribed!")
        setEmail('')
      } else {
        setSubmitStatus('error')
        setSubmitMessage(
          data.message || 'Something went wrong. Please try again.'
        )
      }
    } catch {
      setSubmitStatus('error')
      setSubmitMessage('Network error. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Pages with their own closing conversion section suppress the generic
  // Radar CTA band unless explicitly overridden.
  const shouldShowCTA =
    showCTA ??
    (!pathname?.includes('/vector') &&
      pathname !== '/' &&
      pathname !== '/consulting' &&
      pathname !== '/radar-sites')

  // Home and consulting already render a route-specific contact section.
  const shouldShowStrip =
    showCTA ?? (pathname !== '/' && pathname !== '/consulting')

  const toggleSection = (section: string) => {
    setOpenSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    )
  }

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Projects', href: '/projects' },
    { label: 'About', href: '/about' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blog', href: '/blogs' },
    { label: 'Tools', href: '/tools' },
    { label: 'Labs', href: 'https://labs.pixelmojo.io' },
    { label: 'Press', href: '/press' },
    { label: 'Get in Touch', href: '/contact-us' },
  ]

  const services = [
    {
      label: 'Consulting',
      href: '/consulting',
    },
    {
      label: 'AI Visibility Strategy',
      href: '/services/ai-visibility-strategy',
    },
    {
      label: 'AI Product Development',
      href: '/services/ai-product-development',
    },
    {
      label: 'Brand & Sales Design',
      href: '/services/revenue-first-design',
    },
    {
      label: 'Growth Marketing',
      href: '/services/ai-powered-growth',
    },
  ]

  const socialLinks = [
    {
      icon: FaLinkedinIn,
      href: 'https://www.linkedin.com/company/pixelmojo',
      label: 'LinkedIn',
    },
  ]

  // Responsive Accordion Wrapper Component
  const ResponsiveSection = ({
    title,
    sectionKey,
    children,
    className = '',
  }: {
    title: string
    sectionKey: string
    children: React.ReactNode
    className?: string
  }) => {
    const isOpen = openSections.includes(sectionKey)

    return (
      <div className={className}>
        {/* Mobile Accordion Header - Hidden on lg and up */}
        <button
          onClick={() => toggleSection(sectionKey)}
          className='lg:hidden w-full py-4 flex items-center justify-between text-left transition-colors hover:bg-white/5 border-b border-white/10'
          aria-expanded={isOpen}
          aria-controls={`section-${sectionKey}`}
        >
          <span className='instrument-label text-white/60 leading-none'>
            {title}
          </span>
          <ChevronDown
            className={`h-5 w-5 text-white/60 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Desktop Header - Hidden on mobile, visible on lg and up.
            A real h3 for screen-reader navigation; heading defaults live
            in @layer base since 2026-08-08, so the instrument-label recipe
            (components) and the margin utilities override them cleanly. */}
        <h3 className='hidden lg:block instrument-label text-white/60 mt-0 mb-4'>
          {title}
        </h3>

        {/* Content - Always visible on desktop, accordion on mobile */}
        <div
          id={`section-${sectionKey}`}
          className={`
            lg:block
            ${isOpen ? 'block' : 'hidden lg:block'}
            overflow-hidden transition-all duration-300 ease-in-out
          `}
        >
          <div className='lg:px-0 px-1 pb-4 lg:pb-0'>{children}</div>
        </div>
      </div>
    )
  }

  return (
    <footer className='bg-[var(--surface-dark)] border-t border-white/10'>
      {/* CTA Section — rebuilt 2026-08-08 to the design reference's
          `.final-cta`: a full-bleed magenta band with a 1fr/.58fr grid,
          statement left and the supporting copy plus actions right,
          baseline-aligned. Colour comes from `.accent-surface` in
          globals.css, which owns the whole white-on-magenta ladder
          (label .7, body .78, inverted buttons) so nothing here picks
          alphas by hand. */}
      {/* Home renders the strip itself, before FinalCTA (src/app/(app)/page.tsx). */}
      {shouldShowStrip && <DirectContactStrip />}
      {shouldShowCTA && (
        <div className='accent-surface relative border-b border-white/15'>
          <div className='max-w-shell mx-auto px-4 py-20 lg:py-28'>
            <div className='grid lg:grid-cols-[1fr_0.58fr] gap-10 lg:gap-20 lg:items-end'>
              <div>
                <span className='instrument-label block mb-[18px]'>
                  Two ways to start
                </span>
                {/* thesis-headline sets `margin: 0`, so the gap below comes
                    from the next element, not a mb-* here. */}
                <h2 className='thesis-headline thesis-headline--category'>
                  See how AI sees your brand.
                </h2>
              </div>

              <div>
                <p className='accent-body m-0'>
                  Radar is free. Run it on your domain and see what you find. If
                  you already know you need lead qualification or AI operations,
                  let&apos;s talk. 30 minutes, no pitch deck.
                </p>
                <div className='flex flex-col sm:flex-row gap-3 mt-8'>
                  <LinkButton
                    href='/platform'
                    size='lg'
                    className='group hover:!bg-white hover:!text-[var(--cta-fill)]'
                  >
                    Get My Free Snapshot
                    <Arrow direction='up-right' />
                  </LinkButton>
                  <LinkButton
                    href='/contact-us'
                    size='lg'
                    className='group border border-white/55 bg-transparent text-white hover:bg-white/10'
                  >
                    Book a Strategy Call
                    <Arrow />
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Content */}
      <div className='max-w-shell 2xl:max-w-footer mx-auto px-4 py-12 lg:py-16'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-12'>
          {/* Brand & Newsletter Column - Always visible */}
          <div className='lg:col-span-4 mb-8 lg:mb-0'>
            <div className='mb-8'>
              <Link href='/' className='inline-flex min-h-11 items-center mb-6'>
                {/* No `brightness-0 invert` here: the old flat-fill logo
                    needed it to read white on the dark panel, but this one is
                    all brand colour and a pink-to-blue gradient with no dark
                    ink, so the filter would flatten it to a white silhouette
                    and throw the gradient away. */}
                <Image
                  src='/pixelmojo-branding-new.svg'
                  alt='Pixelmojo'
                  width={150}
                  height={40}
                />
              </Link>
              <p className='text-white/70 mb-6'>
                AI products you own. Intelligence we maintain.
              </p>
              <p className='text-white/45 text-xs leading-relaxed'>
                Pixelmojo · AI product studio · Founded in 2024 by Lloyd Pilapil
                · Makati, Philippines
              </p>
            </div>

            {/* Newsletter */}
            <div className='mb-8'>
              <h3 className='instrument-label text-white/60 mt-0 mb-3'>
                Stay Updated
              </h3>
              <p className='text-white/60 mb-4 text-sm'>
                Build logs, AI agent training insights, and no-BS tactics.
              </p>
              {submitStatus === 'success' ? (
                <div
                  role='status'
                  aria-live='polite'
                  className='p-4 bg-[var(--secondary)]/20 border border-[var(--secondary)]/40'
                >
                  <p className='text-[var(--secondary)] text-sm font-medium'>
                    {submitMessage}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className='flex gap-2'>
                  <Input
                    type='email'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder='Enter your email'
                    tone='inverse'
                    className='flex-1 py-2.5'
                    aria-label='Email for newsletter'
                    disabled={isSubmitting}
                    required
                  />
                  <Button
                    type='submit'
                    size='icon'
                    disabled={isSubmitting || !email}
                    aria-label='Subscribe to newsletter'
                  >
                    {isSubmitting ? (
                      <div
                        className='h-4 w-4 border-2 border-current border-t-transparent animate-spin motion-reduce:animate-none'
                        style={{ borderRadius: '50%' }}
                      />
                    ) : (
                      <Send />
                    )}
                  </Button>
                </form>
              )}
              {submitStatus === 'error' && (
                <p role='alert' className='text-red-400 text-xs mt-2'>
                  {submitMessage}
                </p>
              )}
            </div>

            {/* Social Links */}
            <div className='flex gap-3'>
              {socialLinks.map((social, index) => {
                const Icon = social.icon
                return (
                  <Link
                    key={index}
                    href={social.href}
                    className='w-11 h-11 border border-white/15 hover:border-white/40 hover:bg-white/5 flex items-center justify-center transition-colors duration-200 group'
                    aria-label={social.label}
                  >
                    <Icon className='text-base text-white/70 group-hover:text-white transition-colors' />
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Spacer - Only visible on large screens */}
          <div className='hidden lg:block lg:col-span-1'></div>

          {/* Links Container - Responsive grid within */}
          <div className='lg:col-span-7 border-t lg:border-0 border-white/10'>
            {/* Content-driven tracks: Products/Quick Links hold short labels
                (~80px); Services needs 181px (widest label + 24px hover
                slide) and Get in Touch 175px (icon + email). Equal 1fr
                tracks clip the email at the 2xl footer width. */}
            <div className='grid grid-cols-1 lg:grid-cols-[0.8fr_0.8fr_1.2fr_1.2fr] lg:gap-8'>
              {/* Products - Responsive Section */}
              <ResponsiveSection
                title='Products'
                sectionKey='products'
                className='lg:col-span-1'
              >
                <ul className='space-y-3'>
                  {[
                    { label: 'All Products', href: '/products' },
                    { label: 'Vector', href: '/vector' },
                    { label: 'Hive', href: '/hive' },
                    { label: 'Radar', href: '/platform' },
                    { label: 'Radar Sites', href: '/radar-sites' },
                    {
                      label: 'Radar Sample Report',
                      href: '/platform/sample-report',
                    },
                    { label: 'Brand System', href: '/brand' },
                  ].map((link, index) => (
                    <li key={index}>
                      <Link
                        href={link.href}
                        className='text-white/60 hover:text-white transition-colors duration-200 text-sm min-h-11 lg:min-h-0 flex items-center py-1 lg:py-0'
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </ResponsiveSection>

              {/* Quick Links - Responsive Section */}
              <ResponsiveSection
                title='Quick Links'
                sectionKey='quicklinks'
                className='lg:col-span-1'
              >
                <ul className='space-y-3'>
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <Link
                        href={link.href}
                        className='text-white/60 hover:text-white transition-colors duration-200 text-sm min-h-11 lg:min-h-0 flex items-center py-1 lg:py-0'
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </ResponsiveSection>

              {/* Services - Responsive Section */}
              <ResponsiveSection
                title='Services'
                sectionKey='services'
                className='lg:col-span-1'
              >
                <ul className='space-y-3'>
                  {services.map((service, index) => (
                    <li key={index}>
                      <Link
                        href={service.href}
                        className='text-white/60 hover:text-white transition-colors duration-200 text-sm min-h-11 lg:min-h-0 flex items-center py-1 lg:py-0'
                      >
                        {service.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </ResponsiveSection>

              {/* Contact Info - Responsive Section */}
              <ResponsiveSection
                title='Get in Touch'
                sectionKey='contact'
                className='lg:col-span-1'
              >
                <ul className='space-y-3'>
                  <li>
                    <a
                      href='mailto:founders@pixelmojo.io'
                      className='text-white/60 hover:text-white transition-colors duration-200 text-sm min-h-11 lg:min-h-0 flex items-center group'
                    >
                      <Mail className='h-4 w-4 text-white/40 group-hover:text-white/70 mr-3 flex-shrink-0' />
                      <span>founders@pixelmojo.io</span>
                    </a>
                  </li>
                  <li>
                    <div className='text-white/60 text-sm flex items-start'>
                      <MapPin className='h-4 w-4 text-white/40 mr-3 mt-0.5 flex-shrink-0' />
                      <span>
                        111 Paseo de Roxas, Legazpi Village
                        <br />
                        Makati, 1229 Metro Manila
                      </span>
                    </div>
                  </li>
                </ul>
              </ResponsiveSection>
            </div>
          </div>
        </div>
      </div>

      {/* Built With + bottom bar — compact strip adopted from
          `docs/design-references/adapt-footer.html` (2026-08-11), replacing the
          14-logo grid. Styling is scoped in `footer-adapt.css` under
          `.pm-footer-bands`; type and colour follow the site system. */}
      <div className='pm-footer-bands'>
        <div className='max-w-shell 2xl:max-w-nav mx-auto px-4'>
          <div className='footer-built'>
            Built with
            {BUILT_WITH.map(tool => (
              <span key={tool}>{tool}</span>
            ))}
          </div>

          <div className='footer-bottom'>
            <span>
              &copy; {currentYear} Pixelmojo. All rights reserved. &middot;
              Designed &amp; built with passion &middot;{' '}
              <span className='footer-version'>{getVersion()}</span>
            </span>
            <nav aria-label='Legal'>
              {LEGAL_LINKS.map(link =>
                link.href.startsWith('http') ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.href} href={link.href}>
                    {link.label}
                  </Link>
                )
              )}
              <button type='button' onClick={reopenConsentBanner}>
                Cookie settings
              </button>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  )
}
